import { useState, useRef } from "react";
import { useAuth } from "@clerk/react";
import { Upload, Download, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorBody, getApiErrorMessage } from "@/lib/api-error";
import { apiUrl } from "@/lib/apiBase";

type ParsedRow = {
  firstName: string;
  lastName: string;
  preferredName: string;
  studentCode: string;
  yearLevel: string;
  classNames: string[];
  error?: string;
};

function parseCSV(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const header = lines[0].toLowerCase().split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const colIdx = (names: string[]) => names.map((n) => header.indexOf(n)).find((i) => i >= 0) ?? -1;

  const firstNameIdx     = colIdx(["first_name", "firstname", "first name"]);
  const lastNameIdx      = colIdx(["last_name", "lastname", "last name", "surname"]);
  const preferredNameIdx = colIdx(["preferred_name", "preferredname", "preferred name", "nickname", "nick name"]);
  const studentCodeIdx   = colIdx(["student_code", "studentcode", "student code", "code", "student_id", "student id"]);
  const yearIdx          = colIdx(["year_level", "year level", "year", "grade"]);
  const classIdx         = colIdx(["class", "class_name", "classname", "class name", "group"]);

  // Phase 2: surname column is optional. Only the first-name column is
  // required to start an import.
  if (firstNameIdx === -1) return [];

  const splitLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === "," && !inQuotes) { result.push(current.trim()); current = ""; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };

  return lines.slice(1).map((line) => {
    const cols = splitLine(line);
    const get = (idx: number) =>
      idx >= 0 ? (cols[idx] ?? "").replace(/^"|"$/g, "").trim() : "";
    const firstName     = get(firstNameIdx);
    const lastName      = get(lastNameIdx);
    const preferredName = get(preferredNameIdx);
    const studentCode   = get(studentCodeIdx);
    const yearLevel     = get(yearIdx);
    const rawClass      = get(classIdx);
    const classNames = Array.from(
      new Set(
        rawClass
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    );
    // Surname is now optional — first name is the only thing we need.
    const error = !firstName ? "Missing first name" : undefined;
    return { firstName, lastName, preferredName, studentCode, yearLevel, classNames, error };
  });
}

const TEMPLATE_CSV = `first_name,last_name,preferred_name,student_code,year_level,class
Jane,Smith,,J12345,10,10A
Liam,,Lee,L98765,10,10A
Priya,Patel,,,11,11B|11D
Maya,Chen,,,12,12 Maths|12 Science
`;

function downloadBlob(content: string, filename: string, type = "text/csv") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export type ImportStudentsDialogProps = {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  /** When true, posts schoolOwned: true so students are created as school-owned (head teacher only). */
  schoolOwned?: boolean;
  /** Override the dialog title; defaults to "Import Students from CSV". */
  title?: string;
};

export function ImportStudentsDialog({
  open,
  onClose,
  onDone,
  schoolOwned = false,
  title,
}: ImportStudentsDialogProps) {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; duplicates: string[] } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { getToken } = useAuth();

  const reset = () => { setRows([]); setFileName(""); setResult(null); setImportError(null); };

  const handleFile = (file: File) => {
    setFileName(file.name);
    setResult(null);
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setRows(parseCSV(text));
    };
    reader.readAsText(file);
  };

  const validRows = rows.filter((r) => !r.error);
  const invalidRows = rows.filter((r) => r.error);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    setImportError(null);
    try {
      const token = await getToken();
      let res: Response;
      try {
        res = await fetch(apiUrl("/api/students/import"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({
            students: validRows.map((r) => ({
              firstName: r.firstName,
              // Surname is optional in Phase 2 — send `null` rather than
              // `undefined` so the server clearly stores "no surname".
              lastName: r.lastName || null,
              preferredName: r.preferredName || undefined,
              studentCode: r.studentCode || undefined,
              yearLevel: r.yearLevel || undefined,
              classNames: r.classNames.length > 0 ? r.classNames : undefined,
            })),
            ...(schoolOwned ? { schoolOwned: true } : {}),
          }),
        });
      } catch {
        // api-error-helper-exempt: network-level failure before any HTTP response (no ApiError to extract).
        const msg = "Couldn't reach the server. Please check your connection and try again.";
        setImportError(msg);
        toast({ title: "Import failed", description: msg, variant: "destructive" });
        return;
      }

      if (!res.ok) {
        // Raw fetch path: parse the JSON body manually, then feed it
        // straight into getApiErrorBody (which accepts already-parsed
        // bodies) so the description matches every other onError site.
        let body: unknown = null;
        try { body = await res.json(); } catch { /* not JSON */ }
        const apiBody = getApiErrorBody(body);
        const fallback = `Import failed (${res.status} ${res.statusText || "error"}).`;
        const description = apiBody?.error ?? fallback;
        setImportError(description);
        toast({ title: "Import failed", description, variant: "destructive" });
        return;
      }

      const data = await res.json();
      setResult(data);
      onDone();
    } catch (err) {
      // api-error-helper-exempt: raw fetch (not ApiError); helper produces a sensible fallback.
      const description = getApiErrorMessage(err, "Something went wrong while importing. Please try again.");
      setImportError(description);
      toast({ title: "Import failed", description, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? (schoolOwned ? "Import school-owned students from CSV" : "Import Students from CSV")}</DialogTitle>
        </DialogHeader>

        {schoolOwned && (
          <p className="text-xs text-gray-500 -mt-1">
            Imported students are visible to every teacher at your school.
          </p>
        )}

        {/* Format guide */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Required CSV format</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-blue-600 hover:text-blue-700"
              onClick={() => downloadBlob(TEMPLATE_CSV, "students-template.csv")}
            >
              <Download className="h-3.5 w-3.5" />
              Download template
            </Button>
          </div>
          <div className="font-mono text-xs bg-white border rounded p-2.5 text-gray-600 overflow-x-auto whitespace-pre">
            {`first_name,last_name,preferred_name,student_code,year_level,class\nJane,Smith,,J12345,10,10A\nLiam,,Lee,L98765,10,10A`}
          </div>
          <ul className="mt-2.5 space-y-1 text-xs text-gray-500">
            <li><span className="font-medium text-gray-700">first_name</span> — required. Initials (e.g. <code className="bg-gray-100 px-1 rounded">J.</code>) are accepted if you prefer a privacy-minimal approach.</li>
            <li><span className="font-medium text-gray-700">last_name</span> — optional. Leave blank if you'd rather not record a surname.</li>
            <li><span className="font-medium text-gray-700">preferred_name</span> — optional. Used as the display name when set.</li>
            <li><span className="font-medium text-gray-700">student_code</span> — optional. Your school's student ID or student number, if applicable.</li>
            <li><span className="font-medium text-gray-700">year_level</span> — optional (e.g. 7, 10, 12)</li>
            <li><span className="font-medium text-gray-700">class</span> — optional. The student group you teach, track, or mark together (e.g. <code className="bg-gray-100 px-1 rounded">4 Blue</code>, <code className="bg-gray-100 px-1 rounded">Maths 10.1</code>, <code className="bg-gray-100 px-1 rounded">Stage 6 SLR</code>). Separate multiple with <code className="bg-gray-100 px-1 rounded">|</code>. Classes are created automatically if they don't exist.</li>
          </ul>
        </div>

        {/* File drop zone */}
        {!result && (
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-pink-400 hover:bg-pink-50/30 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="input-import-csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            {fileName ? (
              <div className="flex flex-col items-center gap-1">
                <FileText className="h-8 w-8 text-pink-500 mb-1" />
                <p className="text-sm font-medium text-gray-900">{fileName}</p>
                <p className="text-xs text-gray-500">{rows.length} row{rows.length !== 1 ? "s" : ""} found — click to replace</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Upload className="h-8 w-8 text-gray-300 mb-1" />
                <p className="text-sm font-medium text-gray-700">Drop your CSV here, or click to browse</p>
                <p className="text-xs text-gray-400">.csv files only</p>
              </div>
            )}
          </div>
        )}

        {/* Result state */}
        {result && (
          <div className="space-y-2" data-testid="import-result">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800" data-testid="text-import-created">
                  {result.created} student{result.created !== 1 ? "s" : ""} imported successfully
                </p>
                {result.skipped > 0 && (
                  <p className="text-xs text-green-700" data-testid="text-import-skipped">
                    {result.skipped} row{result.skipped !== 1 ? "s" : ""} skipped (missing name)
                  </p>
                )}
              </div>
            </div>
            {result.duplicates.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="text-import-duplicates">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-sm font-semibold text-amber-800">
                    {result.duplicates.length} duplicate{result.duplicates.length !== 1 ? "s" : ""} skipped
                  </p>
                </div>
                <p className="text-xs text-amber-700 mb-1.5">These students already exist and were not imported again:</p>
                <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
                  {result.duplicates.map((name) => (
                    <li key={name} className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Preview table */}
        {rows.length > 0 && !result && (
          <div className="max-h-56 overflow-y-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {["First Name", "Last Name", "Preferred", "Code", "Year", "Class"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-gray-600">{h}</th>
                  ))}
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row, i) => (
                  <tr key={i} className={row.error ? "bg-red-50" : ""}>
                    <td className="px-3 py-1.5 text-gray-900">{row.firstName || <span className="text-gray-400 italic">—</span>}</td>
                    <td className="px-3 py-1.5 text-gray-900">{row.lastName || <span className="text-gray-400 italic">—</span>}</td>
                    <td className="px-3 py-1.5 text-gray-500">{row.preferredName || "—"}</td>
                    <td className="px-3 py-1.5 text-gray-500 font-mono">{row.studentCode || "—"}</td>
                    <td className="px-3 py-1.5 text-gray-500">{row.yearLevel || "—"}</td>
                    <td className="px-3 py-1.5 text-gray-500">
                      {row.classNames.length === 0 ? (
                        "—"
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {row.classNames.map((n) => (
                            <Badge key={n} variant="secondary" className="text-[10px] font-normal">
                              {n}
                            </Badge>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {row.error && (
                        <span className="flex items-center gap-1 text-red-600">
                          <AlertCircle className="h-3.5 w-3.5" />{row.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && !result && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />{validRows.length} valid
            {invalidRows.length > 0 && <><AlertCircle className="h-3.5 w-3.5 text-red-400 ml-2" />{invalidRows.length} will be skipped</>}
          </div>
        )}

        {importError && !result && (
          <div
            className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
            data-testid="text-import-error"
          >
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{importError}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button
              onClick={handleImport}
              disabled={validRows.length === 0 || importing}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="button-confirm-import"
              data-write-action
            >
              {importing ? "Importing…" : `Import ${validRows.length > 0 ? validRows.length : ""} Student${validRows.length !== 1 ? "s" : ""}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
